import React, { useState, useEffect } from 'react';

interface WaterCraftRequest {
  year?: number;
  craft_type?: string;
  hull_make?: string;
  hull_length?: number;
  year_model?: number;
  body_paint?: string;
  outboard_motor_make?: string;
  outboard_engine_number?: string;
  horse_power?: number;
  max_speed?: number;
  vessel_name?: string;
  vessel_storage?: string;
  vessel_usage?: string;
  amateur_built?: boolean;
  registered_owner?: string;
  finance_house?: string;
  finance_branch?: string;
  hull_value?: number;
  outboard_value?: number;
  sails_value?: number;
  total_sum_insured?: number;
  total_monthly_premium?: number;
  trailer_make?: string;
  trailer_model?: string;
  trailer_registration?: string;
  trailer_value?: number;
  trailer_premium?: number;
  client_id: number;
  quote_id?: number;
}

interface Client {
  id: string;
  name: string;
  isCurrentClient?: boolean;
}

export interface WaterCraft {
  id?: string | number;
  year?: number;
  craftType?: string;
  hullMake?: string;
  hullLength?: number;
  yearModel?: number;
  bodyPaint?: string;
  outboardMotorMake?: string;
  outboardEngineNumber?: string;
  horsePower?: number;
  maxSpeed?: number;
  vesselName?: string;
  vesselStorage?: string;
  vesselUsage?: string;
  amateurBuilt?: boolean;
  registeredOwner?: string;
  financeHouse?: string;
  financeBranch?: string;
  hullValue?: number;
  outboardValue?: number;
  sailsValue?: number;
  totalSumInsured?: number;
  totalMonthlyPremium?: number;
  trailerMake?: string;
  trailerModel?: string;
  trailerRegistration?: string;
  trailerValue?: number;
  trailerPremium?: number;
  isEditing?: boolean;
  isTemp?: boolean;
}

interface WaterCraftErrors {
  year: string;
  craftType: string;
  hullMake: string;
  hullLength: string;
  vesselName: string;
  totalSumInsured: string;
}

interface WaterCraftModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (waterCraftData: WaterCraft) => void;
  clientId: number;
  quoteId?: string;
  waterCraft?: any;
  mode?: 'create' | 'edit' | 'view';
}

// Year Picker Component
interface YearPickerProps {
  value: number | undefined;
  onChange: (year: number) => void;
  disabled?: boolean;
}

const YearPicker: React.FC<YearPickerProps> = ({ value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const startYear = 1900;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);

  const handleYearSelect = (year: number) => {
    onChange(year);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value || ''}
        readOnly
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm cursor-pointer bg-white"
        placeholder="Select year"
        disabled={disabled}
      />
      {!disabled && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      {isOpen && !disabled && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            <div className="py-1">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 ${
                    value === year ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_API_URL;

const WaterCraftModal: React.FC<WaterCraftModalProps> = ({
  isVisible,
  onClose,
  onSave,
  clientId,
  quoteId,
  waterCraft,
  mode = 'create'
}) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

   const [clients, setClients] = useState<Client[]>([]);
   const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Initialize form data
  const getInitialFormData = (): Partial<WaterCraft> => {
    if (isEditMode || isViewMode) {
    if (!waterCraft) return {};
    return {
      id: waterCraft.id,
      year: waterCraft.year,
      craftType: waterCraft.craft_type || waterCraft.craftType,
      hullMake: waterCraft.hull_make || waterCraft.hullMake,
      hullLength: waterCraft.hull_length || waterCraft.hullLength,
      yearModel: waterCraft.year_model || waterCraft.yearModel,
      bodyPaint: waterCraft.body_paint || waterCraft.bodyPaint,
      outboardMotorMake: waterCraft.outboard_motor_make || waterCraft.outboardMotorMake,
      outboardEngineNumber: waterCraft.outboard_engine_number || waterCraft.outboardEngineNumber,
      horsePower: waterCraft.horse_power || waterCraft.horsePower,
      maxSpeed: waterCraft.max_speed || waterCraft.maxSpeed,
      vesselName: waterCraft.vessel_name || waterCraft.vesselName,
      vesselStorage: waterCraft.vessel_storage || waterCraft.vesselStorage,
      vesselUsage: waterCraft.vessel_usage || waterCraft.vesselUsage,
      amateurBuilt: waterCraft.amateur_built ?? waterCraft.amateurBuilt ?? false,
      registeredOwner: waterCraft.registered_owner || waterCraft.registeredOwner,
      financeHouse: waterCraft.finance_house || waterCraft.financeHouse,
      financeBranch: waterCraft.finance_branch || waterCraft.financeBranch,
      hullValue: waterCraft.hull_value || waterCraft.hullValue,
      outboardValue: waterCraft.outboard_value || waterCraft.outboardValue,
      sailsValue: waterCraft.sails_value || waterCraft.sailsValue,
      totalSumInsured: waterCraft.total_sum_insured || waterCraft.totalSumInsured,
      totalMonthlyPremium: waterCraft.total_monthly_premium || waterCraft.totalMonthlyPremium,
      trailerMake: waterCraft.trailer_make || waterCraft.trailerMake,
      trailerModel: waterCraft.trailer_model || waterCraft.trailerModel,
      trailerRegistration: waterCraft.trailer_registration || waterCraft.trailerRegistration,
      trailerValue: waterCraft.trailer_value || waterCraft.trailerValue,
      trailerPremium: waterCraft.trailer_premium || waterCraft.trailerPremium,
    };
  }
    
    // For create mode
    return {
      year: undefined,
      craftType: '',
      hullMake: '',
      hullLength: undefined,
      yearModel: undefined,
      bodyPaint: '',
      outboardMotorMake: '',
      outboardEngineNumber: '',
      horsePower: undefined,
      maxSpeed: undefined,
      vesselName: '',
      vesselStorage: '',
      vesselUsage: '',
      amateurBuilt: false,
      registeredOwner: '',
      financeHouse: '',
      financeBranch: '',
      hullValue: undefined,
      outboardValue: undefined,
      sailsValue: undefined,
      totalSumInsured: undefined,
      totalMonthlyPremium: undefined,
      trailerMake: '',
      trailerModel: '',
      trailerRegistration: '',
      trailerValue: undefined,
      trailerPremium: undefined,
    };
  };

  const [formData, setFormData] = useState<Partial<WaterCraft>>(getInitialFormData);
  const [errors, setErrors] = useState<WaterCraftErrors>({
    year: '',
    craftType: '',
    hullMake: '',
    hullLength: '',
    vesselName: '',
    totalSumInsured: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Currency formatting functions
  const formatCurrencyInput = (value: string | number): string => {
    if (!value && value !== 0) return '';
    const stringValue = typeof value === 'number' ? value.toString() : value;
    const numericValue = stringValue.replace(/[^\d.]/g, '');
    const parts = numericValue.split('.');
    let wholePart = parts[0] || '';
    let decimalPart = parts[1] || '';
    
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    if (wholePart) {
      wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
    }
    
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
  };

  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/,/g, ''));
  };

  // Currency display state
  const [currencyDisplay, setCurrencyDisplay] = useState({
    hullValue: '',
    outboardValue: '',
    sailsValue: '',
    totalSumInsured: '',
    totalMonthlyPremium: '',
    trailerValue: '',
    trailerPremium: '',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isVisible) {
      const initialData = getInitialFormData();
      setFormData(initialData);
      
      setCurrencyDisplay({
        hullValue: formatCurrencyInput(initialData.hullValue?.toString() || ''),
        outboardValue: formatCurrencyInput(initialData.outboardValue?.toString() || ''),
        sailsValue: formatCurrencyInput(initialData.sailsValue?.toString() || ''),
        totalSumInsured: formatCurrencyInput(initialData.totalSumInsured?.toString() || ''),
        totalMonthlyPremium: formatCurrencyInput(initialData.totalMonthlyPremium?.toString() || ''),
        trailerValue: formatCurrencyInput(initialData.trailerValue?.toString() || ''),
        trailerPremium: formatCurrencyInput(initialData.trailerPremium?.toString() || ''),
      });
      
      setIsSubmitting(false);
      setSaveMessage('');
    }
  }, [waterCraft, mode, isVisible]);

  useEffect(() => {
    const fetchOwners = async () => {
      if (!isVisible) return;
      
      setIsLoadingClients(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        // 1. Fetch current client details
        const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/clients/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!clientResponse.ok) {
          const errorText = await clientResponse.text();
          console.error('Fetch current client failed:', clientResponse.status, errorText);
          throw new Error(`Failed to fetch current client: ${clientResponse.status}`);
        }

        const clientData = await clientResponse.json();
        const currentClient = {
          id: clientData.id?.toString() || clientId.toString(),
          name: clientData.name || `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim() || `Client ${clientId}`,
          isCurrentClient: true
        };

        // 2. Fetch persons of interest for this client
        let personsOfInterest: any[] = [];
        try {
          const poiResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (poiResponse.ok) {
            const poiData = await poiResponse.json();
            console.log('Watercraft - Persons of interest data:', poiData);
            
            personsOfInterest = (poiData.data || poiData || []).map((poi: any) => ({
              id: `poi-${poi.id}`,
              // Combine name and surname if both exist
              name: `${poi.name || ''} ${poi.surname || ''}`.trim() || poi.name || poi.surname || `Person of Interest ${poi.id}`,
            })).filter((poi: any) => poi.id && poi.name);
          } else {
            console.warn('Watercraft - Persons of interest fetch failed with status:', poiResponse.status);
          }
        } catch (poiError) {
          console.warn('Watercraft - Could not fetch persons of interest:', poiError);
          // Continue without POIs if this fails
        }

        // 3. Combine current client and persons of interest
        const allOwners: Client[] = [
          currentClient,
          ...personsOfInterest
        ].filter(item => item.id && item.name);

        console.log('Watercraft - Available owners:', allOwners);
        setClients(allOwners);

        // 4. Auto-select current client in create mode if no owner is selected
        if (isCreateMode && !formData.registeredOwner && allOwners.length > 0) {
          setFormData(prev => ({
            ...prev,
            registeredOwner: currentClient.id
          }));
        }

      } catch (error) {
        console.error('Watercraft - Error fetching data:', error);
        setClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    };

    if (isVisible) {
      fetchOwners();
    }
  }, [isVisible, clientId, isCreateMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    if (type !== 'checkbox') {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleYearChange = (year: number) => {
    setFormData(prev => ({
      ...prev,
      year: year,
    }));
    setErrors(prev => ({ ...prev, year: '' }));
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof typeof currencyDisplay) => {
    const { value } = e.target;
    
    const formattedValue = formatCurrencyInput(value);
    const numericValue = parseCurrencyInput(value);
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: numericValue,
    }));
    
    setCurrencyDisplay(prev => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
  };

  const handleCurrencyBlur = (fieldName: keyof typeof currencyDisplay) => {
    const currentValue = currencyDisplay[fieldName];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [fieldName]: formatted }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: WaterCraftErrors = {
      year: !formData.year ? 'Year is required' : '',
      craftType: !formData.craftType ? 'Craft type is required' : '',
      hullMake: !formData.hullMake ? 'Hull make is required' : '',
      hullLength: !formData.hullLength ? 'Hull length is required' : '',
      vesselName: !formData.vesselName ? 'Vessel name is required' : '',
      totalSumInsured: !formData.totalSumInsured ? 'Total sum insured is required' : '',
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSubmitting) return;
  if (!validateForm()) return;

  setIsSubmitting(true);
  setSaveMessage('Saving...');

  // Process registered owner to remove 'poi-' prefix if present
  let processedRegisteredOwner = formData.registeredOwner;
  if (processedRegisteredOwner && processedRegisteredOwner.startsWith('poi-')) {
      processedRegisteredOwner = processedRegisteredOwner.replace('poi-', '');
      console.log('🔧 Stripped poi- prefix from registered owner:', processedRegisteredOwner);
  }

  // Prepare the watercraft data object (without making API calls)
  const watercraftData: WaterCraft = {
    ...formData,
    registeredOwner: processedRegisteredOwner,
    // For new watercraft, create a temporary ID
    id: isEditMode && waterCraft?.id ? waterCraft.id : `temp-${Date.now()}`,
    isTemp: isCreateMode, // Mark as temporary if creating new
    isEditing: false,
  };

  console.log('📦 Prepared watercraft data for parent:', watercraftData);

  try {
    // Pass data back to parent component without saving to DB
    onSave(watercraftData);
    setSaveMessage('Watercraft added to quote successfully!');
    setTimeout(handleClose, 1200);
  } catch (err) {
    console.error('❌ Failed to prepare watercraft:', err);
    setSaveMessage(`Failed to prepare watercraft: ${err instanceof Error ? err.message : 'Unknown error'}`);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleClose = () => {
    setFormData(getInitialFormData());
    setErrors({
      year: '',
      craftType: '',
      hullMake: '',
      hullLength: '',
      vesselName: '',
      totalSumInsured: '',
    });
    setCurrencyDisplay({
      hullValue: '',
      outboardValue: '',
      sailsValue: '',
      totalSumInsured: '',
      totalMonthlyPremium: '',
      trailerValue: '',
      trailerPremium: '',
    });
    setIsSubmitting(false);
    setSaveMessage('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">
          {isViewMode ? 'View Water Craft' : 
           isEditMode ? 'Edit Water Craft' : 'Add Water Craft'}
        </h3>
        
        {saveMessage && (
          <div className={`${saveMessage.includes('❌') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} border px-4 py-3 rounded relative mb-4`}>
            {saveMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 mb-6">
            
            {/* Basic Information */}
            <div className="border-b pb-4">
              <h4 className="text-lg font-semibold mb-3">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">

                    <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                        Year *
                    </label>
                    <YearPicker
                        value={formData.year}
                        onChange={handleYearChange}
                        disabled={isViewMode}
                    />
                    {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
                    </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Type of Craft *
                  </label>
                  <select
                    name="craftType"
                    value={formData.craftType}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  >
                    <option value="">Select craft type</option>
                    <option value="sail">Sail</option>
                    <option value="ski">Ski</option>
                    <option value="motor">Motor</option>
                  </select>
                  {errors.craftType && <p className="text-red-500 text-xs mt-1">{errors.craftType}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Make of Hull *
                  </label>
                  <input
                    type="text"
                    name="hullMake"
                    value={formData.hullMake}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                  {errors.hullMake && <p className="text-red-500 text-xs mt-1">{errors.hullMake}</p>}
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                        Length of Hull (meters) *
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        name="hullLength"
                        value={formData.hullLength || ''}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={isViewMode}
                    />
                    {errors.hullLength && <p className="text-red-500 text-xs mt-1">{errors.hullLength}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Year Model
                  </label>
                  <YearPicker
                    value={formData.yearModel}
                    onChange={(year) => setFormData(prev => ({ ...prev, yearModel: year }))}
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Body Paint
                  </label>
                  <select
                    name="bodyPaint"
                    value={formData.bodyPaint}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  >
                    <option value="">Select paint type</option>
                    <option value="glitter">Glitter</option>
                    <option value="plain">Plain</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Motor Information */}
            <div className="border-b pb-4">
              <h4 className="text-lg font-semibold mb-3">Motor Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Make and Model of Outboard Motor
                  </label>
                  <input
                    type="text"
                    name="outboardMotorMake"
                    value={formData.outboardMotorMake}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Outboard/Engine Number
                  </label>
                  <input
                    type="text"
                    name="outboardEngineNumber"
                    value={formData.outboardEngineNumber}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                        Horse Power
                    </label>
                    <input
                        type="number"
                        name="horsePower"
                        value={formData.horsePower || ''}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={isViewMode}
                    />
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                        Max Speed (km/h)
                    </label>
                    <input
                        type="number"
                        name="maxSpeed"
                        value={formData.maxSpeed || ''}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={isViewMode}
                    />
                </div>

              </div>
            </div>

            {/* Vessel Information */}
            <div className="border-b pb-4">
              <h4 className="text-lg font-semibold mb-3">Vessel Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Name of Vessel *
                  </label>
                  <input
                    type="text"
                    name="vesselName"
                    value={formData.vesselName}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                  {errors.vesselName && <p className="text-red-500 text-xs mt-1">{errors.vesselName}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Where will vessel be kept?
                  </label>
                  <input
                    type="text"
                    name="vesselStorage"
                    value={formData.vesselStorage}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Where will vessel be used?
                  </label>
                  <input
                    type="text"
                    name="vesselUsage"
                    value={formData.vesselUsage}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="amateurBuilt"
                      checked={formData.amateurBuilt || false}
                      onChange={handleInputChange}
                      className="mr-2"
                      disabled={isViewMode}
                    />
                    Was vessel built by an amateur?
                  </label>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Registered Owner
                  </label>
                  <select
                    name="registeredOwner"
                    value={formData.registeredOwner}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isLoadingClients || isViewMode}
                  >
                    <option value="" disabled>Select an owner</option>
                    {isLoadingClients ? (
                      <option value="" disabled>Loading owners...</option>
                    ) : clients.length === 0 ? (
                      <option value="" disabled>No owners available</option>
                    ) : (
                      clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Finance House
                  </label>
                  <input
                    type="text"
                    name="financeHouse"
                    value={formData.financeHouse}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Branch
                  </label>
                  <input
                    type="text"
                    name="financeBranch"
                    value={formData.financeBranch}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </div>

            {/* Insurance Values */}
            <div className="border-b pb-4">
              <h4 className="text-lg font-semibold mb-3">Insurance Values</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Value of Hull and Fixed Equipment
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.hullValue}
                      onChange={(e) => handleCurrencyChange(e, 'hullValue')}
                      onBlur={() => handleCurrencyBlur('hullValue')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Value of Outboard Motors
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.outboardValue}
                      onChange={(e) => handleCurrencyChange(e, 'outboardValue')}
                      onBlur={() => handleCurrencyBlur('outboardValue')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Value of Sails, Masts and Rigging
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.sailsValue}
                      onChange={(e) => handleCurrencyChange(e, 'sailsValue')}
                      onBlur={() => handleCurrencyBlur('sailsValue')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Total Sum Insured *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.totalSumInsured}
                      onChange={(e) => handleCurrencyChange(e, 'totalSumInsured')}
                      onBlur={() => handleCurrencyBlur('totalSumInsured')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                  {errors.totalSumInsured && <p className="text-red-500 text-xs mt-1">{errors.totalSumInsured}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Total Monthly Premium
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.totalMonthlyPremium}
                      onChange={(e) => handleCurrencyChange(e, 'totalMonthlyPremium')}
                      onBlur={() => handleCurrencyBlur('totalMonthlyPremium')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Trailer Information */}
            <div>
              <h4 className="text-lg font-semibold mb-3">Trailer Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Trailer Make
                  </label>
                  <input
                    type="text"
                    name="trailerMake"
                    value={formData.trailerMake}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Trailer Model
                  </label>
                  <input
                    type="text"
                    name="trailerModel"
                    value={formData.trailerModel}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Trailer Registration Number
                  </label>
                  <input
                    type="text"
                    name="trailerRegistration"
                    value={formData.trailerRegistration}
                    onChange={handleInputChange}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Trailer Value
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.trailerValue}
                      onChange={(e) => handleCurrencyChange(e, 'trailerValue')}
                      onBlur={() => handleCurrencyBlur('trailerValue')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Trailer Monthly Premium
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={currencyDisplay.trailerPremium}
                      onChange={(e) => handleCurrencyChange(e, 'trailerPremium')}
                      onBlur={() => handleCurrencyBlur('trailerPremium')}
                      className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                      placeholder="0.00"
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
              disabled={isSubmitting}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 
                isEditMode ? 'Update Water Craft' : 'Save Water Craft'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default WaterCraftModal;